// T2: Intercom management hub.
//
// Covers Stitch v2 designs `intercom_management_northgate_park` (PNG
// only — layout derived) and `intercom_management_operational_view`.
// Single page; the two slugs differ only in the variant headline
// emphasis. Hub links to live operations, monitoring, and exposes a
// rolled-up summary of recent activity per callbox.

import { Link } from 'react-router-dom';
import { Badge, Card, CardBody, Icon, Spinner } from '@ng-unifi/ui';
import { useIntercomCalls, statusLabel, statusTone } from '../lib/intercomCalls.js';

export function IntercomManagementPage() {
  const { calls, error, loading } = useIntercomCalls();

  if (loading) {
    return (
      <div className="p-12 flex justify-center">
        <Spinner />
      </div>
    );
  }

  const counts = {
    queued: (calls ?? []).filter((c) => c.status === 'queued').length,
    answered: (calls ?? []).filter((c) => c.status === 'answered').length,
    ended: (calls ?? []).filter((c) => c.status === 'ended').length,
    missed: (calls ?? []).filter((c) => c.status === 'missed').length,
  };

  // Per-callbox last-5 summary.
  const byCallbox = new Map<string, typeof calls extends (infer T)[] | null ? T : never>();
  for (const c of calls ?? []) {
    if (!byCallbox.has(c.callbox_id)) {
      byCallbox.set(c.callbox_id, c);
    }
  }

  const tiles: Array<{ label: string; value: number; tone: 'warning' | 'success' | 'danger' | 'neutral' }> = [
    { label: 'Ringing', value: counts.queued, tone: 'warning' },
    { label: 'In call', value: counts.answered, tone: 'success' },
    { label: 'Recent missed', value: counts.missed, tone: 'danger' },
    { label: 'Recent ended', value: counts.ended, tone: 'neutral' },
  ];

  return (
    <div className="space-y-6">
      {/* flex-wrap: title plus two "→" links do not fit a 320px row. */}
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold">Intercom</h1>
          <p className="text-on-surface-variant">
            Management hub for all callbox activity in this property.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to="/intercom/ops" className="text-primary underline self-center">
            Live ops →
          </Link>
          <Link to="/intercom/monitor" className="text-primary underline self-center">
            Monitoring →
          </Link>
        </div>
      </header>

      {error ? (
        <p role="alert" className="text-error">
          <Icon name="error" /> {error}
        </p>
      ) : null}

      <div className="grid gap-4 grid-cols-2 sm:grid-cols-4">
        {tiles.map((t) => (
          <Card key={t.label}>
            <CardBody>
              <p className="text-sm text-on-surface-variant">{t.label}</p>
              <p className="text-3xl font-semibold mt-1">{t.value}</p>
              <Badge tone={t.tone} className="mt-2">
                {t.label}
              </Badge>
            </CardBody>
          </Card>
        ))}
      </div>

      <section>
        <h2 className="text-lg font-medium mb-2">Per-callbox latest activity</h2>
        <Card>
          <CardBody>
            {byCallbox.size === 0 ? (
              <p className="py-3 text-on-surface-variant">No callbox activity yet.</p>
            ) : (
              <ul className="divide-y divide-outline-variant">
                {Array.from(byCallbox.values()).map((c) => (
                  <li key={c.callbox_id} className="flex items-center gap-3 py-3">
                    <div className="flex-1">
                      <p className="font-medium">Callbox {c.callbox_id.slice(0, 8)}</p>
                      <p className="text-sm text-on-surface-variant">
                        Last call: {new Date(c.initiated_at).toLocaleString()}
                      </p>
                    </div>
                    <Badge tone={statusTone(c.status)}>{statusLabel(c.status)}</Badge>
                    <Link
                      to={`/intercom/ops`}
                      className="text-primary underline text-sm"
                    >
                      View
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>
      </section>
    </div>
  );
}
